'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { clearAuthStorage, isExpectedAuthError } from '@/lib/supabase/client';

export type UserRole = 'super_admin' | 'admin_it' | 'tecnico' | 'operatore' | 'sola_lettura';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  reparto: string;
  telefono: string;
  is_active: boolean;
  struttura_id?: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  getCurrentUser: () => Promise<any>;
  getUserProfile: () => Promise<UserProfile | null>;
  isEmailVerified: () => boolean;
  canWrite: () => boolean;
  canAdmin: () => boolean;
  roleLabel: () => string;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  // Singleton client — no useMemo needed, createClient() always returns the same instance
  const supabase = createClient();

  // Track last profile fetch to debounce rapid repeated calls
  const lastProfileFetch = useRef<{ userId: string; ts: number } | null>(null);
  const PROFILE_DEBOUNCE_MS = 5000; // don't re-fetch profile for same user within 5s

  const fetchProfile = async (userId: string) => {
    // Debounce: skip if we fetched this user's profile recently
    const now = Date.now();
    if (
      lastProfileFetch.current?.userId === userId &&
      now - lastProfileFetch.current.ts < PROFILE_DEBOUNCE_MS
    ) {
      return;
    }
    lastProfileFetch.current = { userId, ts: now };

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (!error && data) setProfile(data as UserProfile);
    } catch {}
  };

  useEffect(() => {
    // Use onAuthStateChange exclusively — it fires immediately with the current
    // session (INITIAL_SESSION event), so a separate getSession() call is not
    // needed and would race for the same IndexedDB auth lock causing AbortError.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const event = _event as string;
      if (event === 'TOKEN_REFRESH_FAILED' || event === 'SIGNED_OUT') {
        clearAuthStorage();
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
        if (typeof window !== 'undefined') {
          const publicPaths = ['/login', '/register', '/registrati', '/forgot-password', '/reset-password', '/prezzi', '/report-pubblico'];
          const isPublic = publicPaths.some(p => window.location.pathname.startsWith(p));
          if (!isPublic && event === 'TOKEN_REFRESH_FAILED') {
            window.location.href = '/login';
          }
        }
        return;
      }

      // Ignore INITIAL_SESSION when there's no session — avoids unnecessary state churn
      if (_event === 'INITIAL_SESSION' && !session) {
        setLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Use setTimeout to defer profile fetch so it doesn't block the auth state update
        // and doesn't compete with the token refresh cycle
        setTimeout(() => fetchProfile(session.user.id), 0);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: (metadata as any)?.fullName || '', ...(metadata as any) },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      // Ignore expected errors (e.g. already signed out / invalid token)
      if (!isExpectedAuthError(err)) throw err;
    }
    clearAuthStorage();
  };

  const getCurrentUser = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        if (isExpectedAuthError(error)) return null;
        throw error;
      }
      return user;
    } catch (err) {
      if (isExpectedAuthError(err)) return null;
      throw err;
    }
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data as UserProfile;
  };

  const canWrite = () => {
    const role = profile?.role;
    return role === 'super_admin' || role === 'admin_it' || role === 'tecnico';
  };

  const canAdmin = () => {
    const role = profile?.role;
    return role === 'super_admin' || role === 'admin_it';
  };

  const roleLabel = () => {
    const labels: Record<UserRole, string> = {
      super_admin: 'Super Admin',
      admin_it: 'Admin IT',
      tecnico: 'Tecnico',
      operatore: 'Operatore',
      sola_lettura: 'Sola Lettura',
    };
    return profile?.role ? labels[profile.role] : 'Utente';
  };

  return (
    <AuthContext.Provider value={{
      user, session, profile, loading,
      signIn, signOut, signUp,
      getCurrentUser, getUserProfile, isEmailVerified,
      canWrite, canAdmin, roleLabel,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
